// Local Imports
import { model } from "GenAlphaModel";
import { MousePos } from "../../../mousepos";
import { EvalLabel, SymbolPin } from "../../shared";
import { Base } from "./base";
import type { DrawTo } from "../../../draw";
import * as style from "../../../style";
import { StyleTables } from "../../shared/styles";
import { Args, Entity } from "../../../tree";
import { SelectionBox } from "./selbox";

//
// # Resolved (Symbol) Instance Data
//
// Type alias for instances with resolved symbols.
// Primarily asserts that `symbol` is defined.
//
export type Data = Omit<model.Instance, "symbol"> & {
  symbol: model.Symbol;
};

// # Resolved Instance
//
export class ResolvedInstance
  extends Base<Data>
  implements model.schematic.Xformable
{
  static readonly instanceKind = "resolved" as const;

  constructor(args: Args<Data>) {
    super(args);
    this.build();
  }

  // Children
  readonly evalLabels: Set<EvalLabel> = new Set();
  readonly pins: Set<SymbolPin> = new Set();
  public symbol!: InstanceSymbol;
  readonly selectionBoxEntities = new Set<SelectionBox>();

  // Selection Boxes
  // The symbol-space bboxes which serve as this instance's selection area.
  // See `computeSelectionBboxes` for how they're resolved.
  private selectionBboxes: Array<model.Bbox> = [];

  // `selectionBboxes`, transformed into world space. Used for both point
  // hit-testing and bbox-intersection selection.
  private selectionRegions: Array<model.Bbox> = [];

  // Clear out all our sub data stores
  override clear = () => {
    super.clear();
    this.evalLabels.clear();
    this.pins.clear();
    this.selectionBoxEntities.clear();
    this.selectionBboxes = [];
    this.selectionRegions = [];
  };

  // Build our sub data stores from our `data`.
  override build = () => {
    const { data } = this;
    this.symbol = new InstanceSymbol({
      data: this.data.symbol,
      parent: this,
    });
    // Add the labels
    data.evals.map((labeldata) => {
      const evalLabel = new EvalLabel({
        data: labeldata,
        parent: this,
      });
      this.evalLabels.add(evalLabel);
    });
    // Add the pins
    for (const pin of data.symbol.pins) {
      const pinDrawing = new SymbolPin({ data: pin, parent: this });
      this.pins.add(pinDrawing);
    }
    // Resolve the symbol-space selection area, applying the bbox fallback for
    // symbols with no explicit selection boxes. Computed once here, then shared
    // by the drawn `SelectionBox` entities and by `updateBbox`.
    this.selectionBboxes = this.computeSelectionBboxes(data.symbol);
    // Add the selection box entities
    this.selectionBboxes.forEach((bbox) => {
      const e = new SelectionBox({
        data: bbox,
        parent: this,
      });
      this.selectionBoxEntities.add(e);
    });
    this.updateBbox();
  };

  // The symbol-space bboxes which make up this instance's selection area.
  //
  // Prefers the symbol's explicit `selectionBoxes`. A symbol with none of them
  // falls back to its bounding box, so that every drawn symbol is selectable
  // (and shows a highlight box) whether or not whoever authored it bothered to
  // include an explicit selection box.
  private computeSelectionBboxes = (
    symbol: model.Symbol
  ): Array<model.Bbox> => {
    if (symbol.selectionBoxes.length > 0) {
      return symbol.selectionBoxes;
    }
    // No explicit selection boxes — fall back to the drawn-graphics bbox,
    // which excludes pins and label locations and so is much tighter than
    // `symbol.bbox` for most builtin/PDK-style symbols.
    const graphicsBbox = model.Symbol.graphicsBbox(symbol);
    if (graphicsBbox) {
      return [graphicsBbox];
    }
    // Last-resort fallback: the symbol's overall bbox.
    if (symbol.bbox) {
      return [symbol.bbox];
    }
    return [];
  };

  // Draw the Instance's `drawn`.
  override redraw = (to: DrawTo): unknown => {
    return to.group(this.data.xform, this.redraw_);
  };

  // The part of `redraw` that happens inside our transformed coordinate space
  private readonly redraw_ = (to: DrawTo): unknown => {
    this.symbol.redraw(to);
    this.selectionBoxEntities.forEach((e) => e.redraw(to));
    this.pins.forEach((pin) => pin.redraw(to));
    return this.evalLabels.forEach((evalLabel) => evalLabel.redraw(to));
  };

  // Boolean indication of whether `mousePos` is inside the Instance's bounding box.
  // Tests against the resolved selection regions (see `selectionRegions`).
  hitTest = (mousePos: MousePos): boolean => {
    return this.selectionRegions.some((region) =>
      model.Bbox.contains(region, mousePos.sch)
    );
  };

  // Boolean indication of whether `bbox` intersects any of this Instance's
  // selection regions. Used for rubber-band / box-drag selection.
  intersectsBbox = (bbox: model.Bbox): boolean => {
    return this.selectionRegions.some((region) =>
      model.Bbox.intersects(bbox, region)
    );
  };

  // Update the Instance's selection regions.
  // Applies the instance transformation to `selectionBboxes`, which `build`
  // has already resolved.
  updateBbox = () => {
    this.selectionRegions = this.selectionBboxes.map((bbox) =>
      model.Xform.transformBbox(this.xform, bbox)
    );
  };
}

class InstanceSymbol extends Entity<model.Symbol> {
  override readonly cache = this.makeCache<style.Style>(StyleTables.symbol);

  // Draw the symbol's graphics (only). Not the pins, labels, etc.
  override redraw = (to: DrawTo): unknown => {
    return to.elements(this.data.graphics, this.cache.style, this);
  };
}
